import React from 'react'
import styled from 'styled-components'
import Button from '@material-ui/core/Button'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import {PlanInfo, FOUNDER, PlanName} from 'obvio/Billing/plans'
import {usePlan} from 'obvio/Billing/PlanProvider'
import {BillingInterval} from 'obvio/Billing/plans'
import {useGetSubscription} from 'obvio/Billing/subscribe'
import {obvioRoutes} from 'obvio/Routes'
import {colors} from 'lib/ui/theme'

export default function PlanActionButton(props: {
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {interval, plan} = props
  const {
    canCancel,
    canDowngrade,
    canResume,
    canUpgrade,
    isDowngradingTo,
  } = usePlan()
  const subscription = useGetSubscription()

  if (isDowngradingTo(plan, subscription)) {
    return null
  }

  if (canResume(plan, subscription)) {
    return <ResumePlanButton interval={interval} plan={plan} />
  }

  if (canCancel(plan, subscription)) {
    return <CancelPlanButton interval={interval} plan={plan} />
  }

  if (canUpgrade(plan, subscription)) {
    return <UpgradePlanButton interval={interval} plan={plan} />
  }

  if (canDowngrade(plan, subscription)) {
    return <DowngradePlanButton interval={interval} plan={plan} />
  }

  return <ChoosePlanButton interval={interval} plan={plan} />
}

const StyledLink = styled((props) => {
  const {children, ...otherProps} = props

  return (
    <RelativeLink disableStyles {...otherProps}>
      {children}
    </RelativeLink>
  )
})<{
  children: React.ReactElement | React.ReactElement[]
}>`
  width: 100%;
`

const planUrl = (
  route: string,
  planName: PlanName,
  interval: BillingInterval,
): string => {
  let url = ''

  switch (route) {
    case 'cancel':
      url = `${obvioRoutes.billing.cancel_plan}?plan=${planName}`
      break
    case 'resume':
      url = `${obvioRoutes.billing.resume_plan}?plan=${planName}`
      break
    case 'downgrade':
      url = `${obvioRoutes.billing.change_plan}?plan=${planName}&downgrade`
      break
    case 'upgrade':
    case 'choose':
      url = `${obvioRoutes.billing.change_plan}?plan=${planName}`
      break
  }

  return `${url}&interval=${interval}`
}

function CancelPlanButton(props: {interval: BillingInterval; plan: PlanInfo}) {
  const {interval, plan} = props
  const cancelUrl = planUrl('cancel', plan.name, interval)

  return (
    <StyledLink to={cancelUrl}>
      <StyledButton
        color={colors.error}
        aria-label={`cancel ${plan.name} plan`}
      >
        Cancel Plan
      </StyledButton>
    </StyledLink>
  )
}

function ResumePlanButton(props: {interval: BillingInterval; plan: PlanInfo}) {
  const {interval, plan} = props
  const resumeUrl = planUrl('resume', plan.name, interval)

  return (
    <StyledLink to={resumeUrl}>
      <StyledButton
        color={colors.primary}
        aria-label={`resume ${plan.name} plan`}
      >
        Resume Plan
      </StyledButton>
    </StyledLink>
  )
}

function UpgradePlanButton(props: {interval: BillingInterval; plan: PlanInfo}) {
  const {interval, plan} = props
  const upgradeUrl = planUrl('upgrade', plan.name, interval)

  if (plan.name === FOUNDER) {
    return <ChoosePlanButton interval={interval} plan={plan} />
  }

  return (
    <StyledLink to={upgradeUrl}>
      <StyledButton aria-label={`upgrade ${plan.name} plan`}>
        Upgrade Plan
      </StyledButton>
    </StyledLink>
  )
}

function ChoosePlanButton(props: {interval: BillingInterval; plan: PlanInfo}) {
  const {interval, plan} = props
  const upgradeUrl = planUrl('choose', plan.name, interval)

  return (
    <StyledLink to={upgradeUrl}>
      <StyledButton aria-label={`choose ${plan.name} plan`}>
        Choose Plan
      </StyledButton>
    </StyledLink>
  )
}

function DowngradePlanButton(props: {
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {interval, plan} = props
  const downgradeUrl = planUrl('downgrade', plan.name, interval)

  return (
    <StyledLink to={downgradeUrl}>
      <StyledButton aria-label={`downgrade ${plan.name} plan`}>
        Downgrade Plan
      </StyledButton>
    </StyledLink>
  )
}

const StyledButton = styled((props) => {
  const {children, color: _, ...otherProps} = props

  return (
    <Button variant="outlined" fullWidth {...otherProps}>
      {children}
    </Button>
  )
})<{
  color: string
}>`
  background-color: transparent !important;
  border: solid 1px ${(props) => props.color};
  color: ${(props) => props.color};
  margintop: 9px;
`
